/* eslint-disable react/jsx-max-props-per-line */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LineChart } from '@mui/x-charts/LineChart';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { UNIDADE_LABELS } from './constants';

const CHART_HEIGHT = 180;

const buildDayLabels = (count) => Array.from({ length: count }, (_, index) => {
  const daysAgo = count - 1 - index;
  if (daysAgo === 0) return 'Hoje';
  if (daysAgo === 1) return 'Ontem';
  return moment().subtract(daysAgo, 'days').format('DD/MM');
});

const computeYMax = (values, ...metas) => {
  const peak = Math.max(...values, 1);
  const metaMax = Math.max(0, ...metas.filter((meta) => meta != null && Number.isFinite(meta)));
  const withMeta = Math.max(peak * 1.15, metaMax);
  if (withMeta <= 40) return Math.ceil(withMeta / 5) * 5;
  if (withMeta <= 100) return Math.ceil(withMeta / 10) * 10;
  if (withMeta <= 200) return Math.ceil(withMeta / 20) * 20;
  return Math.ceil(withMeta / 50) * 50;
};

const hasDigitacao = (serie) =>
  Array.isArray(serie)
  && serie.length > 0
  && serie.some((v) => Number(v) > 0);

const RitmoTrendChart = ({ ultimos7Dias, metaDiaria, alvoDiario, unidade }) => {
  const theme = useTheme();
  const unitLabel = UNIDADE_LABELS[unidade] ?? 'serviços';
  const labels = useMemo(() => buildDayLabels(ultimos7Dias.length || 7), [ultimos7Dias.length]);
  const showPessoal = alvoDiario != null
    && metaDiaria != null
    && alvoDiario !== metaDiaria;
  const yMax = useMemo(
    () => computeYMax(ultimos7Dias, metaDiaria, showPessoal ? alvoDiario : null),
    [ultimos7Dias, metaDiaria, alvoDiario, showPessoal],
  );

  if (!hasDigitacao(ultimos7Dias)) {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: CHART_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1,
          py: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Sem digitação nos últimos 7 dias
        </Typography>
        {metaDiaria != null && (
          <Typography variant="caption" color="text.disabled" textAlign="center" sx={{ mt: 0.5 }}>
            Meta empresa: {metaDiaria}/{unitLabel} por dia útil
            {showPessoal ? ` · pessoal ${alvoDiario}` : ''}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <LineChart
        height={CHART_HEIGHT}
        margin={{ left: 8, right: 12, top: 20, bottom: 24 }}
        grid={{ horizontal: true }}
        xAxis={[{
          scaleType: 'point',
          data: labels,
          tickLabelStyle: { fontSize: 11 },
        }]}
        yAxis={[{
          min: 0,
          max: yMax,
          width: 32,
          tickLabelStyle: { fontSize: 11 },
        }]}
        series={[{
          data: ultimos7Dias,
          label: unitLabel,
          showMark: true,
          curve: 'monotoneX',
          valueFormatter: (value) => `${value} ${unitLabel}`,
        }]}
        slotProps={{
          legend: { sx: { display: 'none' } },
        }}
        sx={{ width: '100%' }}
      >
        {metaDiaria != null && (
          <ChartsReferenceLine
            y={metaDiaria}
            label={String(metaDiaria)}
            lineStyle={{
              strokeDasharray: '4 4',
              stroke: theme.palette.warning.main,
              strokeWidth: 1.5,
            }}
            labelStyle={{
              fontSize: 10,
              fontWeight: 700,
              fill: theme.palette.warning.main,
            }}
          />
        )}
        {showPessoal && (
          <ChartsReferenceLine
            y={alvoDiario}
            label={String(alvoDiario)}
            lineStyle={{
              strokeDasharray: '4 4',
              stroke: theme.palette.info.main,
              strokeWidth: 1.5,
            }}
            labelStyle={{
              fontSize: 10,
              fontWeight: 700,
              fill: theme.palette.info.main,
            }}
          />
        )}
      </LineChart>
    </Box>
  );
};

RitmoTrendChart.propTypes = {
  ultimos7Dias: PropTypes.arrayOf(PropTypes.number).isRequired,
  metaDiaria: PropTypes.number,
  alvoDiario: PropTypes.number,
  unidade: PropTypes.oneOf(['servicos', 'postes']),
};

RitmoTrendChart.defaultProps = {
  metaDiaria: null,
  alvoDiario: null,
  unidade: 'servicos',
};

export default RitmoTrendChart;
